import React, { useState, useEffect, useContext } from "react";
import { useAuth } from "../services/use-auth";
import { signInWithEmailAndPassword } from "firebase/auth";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import * as ROUTES from "../constants/routes";
import { tracedCallable } from "../utilities/tracedCallable";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";

import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import Button from "../components/Button";
import UserContext from "../assets/user_context";

import AuthLayout from "./AuthLayout";
import { FormCard, AuthTitle, TextLink, ErrorText } from "./authStyles";

// Check and sync email after sign-in (handles verification while logged out)
const checkAndSyncEmailAfterSignIn = async (user, authHook, setNotification) => {
  try {
    console.log("Checking email sync after sign-in...");

    const userDocRef = doc(authHook.db, `users/${user.uid}`);
    const userDoc = await getDoc(userDocRef);

    if (userDoc.exists()) {
      const userData = userDoc.data();

      if (user.email !== userData.email) {
        console.log(`Email mismatch: ${userData.email} → ${user.email}`);

        await updateDoc(
          userDocRef,
          {
            email: user.email,
            emailSyncedAt: new Date().toISOString(),
          },
          { merge: true },
        );

        window.emailJustSyncedOnSignIn = true;

        setNotification({
          open: true,
          severity: "success",
          message: `Email successfully verified and updated to ${user.email}!`,
        });

        setTimeout(() => {
          delete window.emailJustSyncedOnSignIn;
        }, 2000);
      }
    }
  } catch (error) {
    console.error("Error syncing email after sign-in:", error);
  }
};

const SignInComponent = () => {
  const authHook = useAuth();
  const navigate = useNavigate();
  const { setUserStillLoading } = useContext(UserContext);
  const theme = useTheme();

  const [loginData, setLoginData] = useState({ email: "", password: "" });
  const [entryErrors, setEntryErrors] = useState({
    email: false,
    password: false,
  });
  const [entryFeedback, setEntryFeedback] = useState({
    email: null,
    password: null,
  });
  const [errorFeedback, setErrorFeedback] = useState(null);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    severity: "info",
    message: "",
  });

  useEffect(() => {
    if (authHook?.auth?.currentUser) {
      navigate(ROUTES.LANDING);
    }
  }, [authHook?.auth?.currentUser, navigate]);

  // Show "Forgot your password?" after 45 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowForgotPassword(true);
    }, 45000);
    return () => clearTimeout(timer);
  }, []);

  // Show "Forgot your password?" for very short passwords
  useEffect(() => {
    if (loginData.password.length > 0 && loginData.password.length < 6) {
      setShowForgotPassword(true);
    }
  }, [loginData.password]);

  const handleChange = (event) => {
    setLoginData((prev) => ({
      ...prev,
      [event.target.name]: event.target.value,
    }));

    if (entryErrors.email && event.target.name === "email") {
      validateEmail();
    }
    if (entryErrors.password && event.target.name === "password") {
      validatePassword();
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !invalid) {
      event.preventDefault();
      submitSignIn();
    }
  };

  const validateEmail = () => {
    if (loginData.email === "") {
      setEntryErrors((prev) => ({ ...prev, email: true }));
      setEntryFeedback((prev) => ({ ...prev, email: "You must enter a email" }));
    } else {
      const containsAtAndPeriod =
        loginData.email.includes("@") && loginData.email.includes(".");
      if (!containsAtAndPeriod) {
        setEntryErrors((prev) => ({ ...prev, email: true }));
        setEntryFeedback((prev) => ({
          ...prev,
          email: "You must enter a valid email address",
        }));
      } else {
        setEntryErrors((prev) => ({ ...prev, email: false }));
        setEntryFeedback((prev) => ({ ...prev, email: null }));
      }
    }
  };

  const validatePassword = () => {
    if (loginData.password === "") {
      setEntryErrors((prev) => ({ ...prev, password: true }));
      setEntryFeedback((prev) => ({
        ...prev,
        password: "You must enter a password",
      }));
    } else {
      setEntryErrors((prev) => ({ ...prev, password: false }));
      setEntryFeedback((prev) => ({ ...prev, password: null }));
    }
  };

  const submitSignIn = async () => {
    setProcessing(true);
    try {
      const userCredential = await signInWithEmailAndPassword(
        authHook.auth,
        loginData.email,
        loginData.password,
      );

      const user = userCredential.user;
      await checkAndSyncEmailAfterSignIn(user, authHook, setNotification);

      const redirectUrl = sessionStorage.getItem("redirectAfterLogin");
      if (redirectUrl) {
        sessionStorage.removeItem("redirectAfterLogin");
        navigate(redirectUrl);
      } else {
        navigate(ROUTES.REPORTS);
      }
    } catch (error) {
      const errorCode = error.code;
      console.error(errorCode, error.message);

      let feedbackMessage;

      switch (errorCode) {
        case "auth/invalid-email":
          feedbackMessage = "Invalid email format.";
          break;
        case "auth/user-disabled":
          feedbackMessage = "User account is disabled.";
          break;
        case "auth/user-not-found":
          feedbackMessage = "Email not found.";
          break;
        case "auth/wrong-password":
          feedbackMessage = "Incorrect password.";
          break;
        case "auth/email-already-in-use":
          feedbackMessage = "Email already in use.";
          break;
        case "auth/weak-password":
          feedbackMessage = "Weak password.";
          break;
        case "auth/account-exists-with-different-credential":
          feedbackMessage = "Account exists with different credentials.";
          break;
        case "auth/invalid-credential":
          feedbackMessage = "Invalid credentials.";
          break;
        case "auth/operation-not-allowed":
          feedbackMessage = "Operation not allowed.";
          break;
        case "auth/network-request-failed":
          feedbackMessage = "Network error occurred.";
          break;
        case "auth/too-many-requests":
          feedbackMessage = "Too many requests. Slow down!";
          break;
        case "auth/requires-recent-login":
          feedbackMessage = "Recent login required.";
          break;
        default:
          feedbackMessage = "An unknown error occurred. Check console.";
      }

      setErrorFeedback(feedbackMessage);
    } finally {
      setProcessing(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!loginData.email) {
      setNotification({
        open: true,
        severity: "warning",
        message: "Please enter your email address first.",
      });
      return;
    }

    try {
      const sendReset = tracedCallable(
        authHook.functions,
        "sendSelfServicePasswordReset",
      );
      await sendReset({ email: loginData.email });
      // Enumeration-safe copy: never confirm whether the account exists.
      setNotification({
        open: true,
        severity: "success",
        message:
          "If an account exists for that email, we've sent a password reset link. Check your inbox and spam folder.",
      });
      setErrorFeedback(null);
    } catch (error) {
      console.error("Password reset error:", error);
      if (error.code === "functions/resource-exhausted") {
        setNotification({
          open: true,
          severity: "warning",
          message:
            "Too many requests. Please wait a few minutes before trying again.",
        });
      } else {
        setNotification({
          open: true,
          severity: "error",
          message: "Error sending reset email. Please try again.",
        });
      }
    }
  };

  const invalid =
    loginData.email === "" ||
    loginData.password === "" ||
    entryErrors.email ||
    entryErrors.password;

  return (
    <AuthLayout>
      <FormCard>
        <AuthTitle theme={theme}>Sign In</AuthTitle>
        <Box
          component="form"
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
            pb: 3,
            width: "100%",
          }}
          noValidate
          autoComplete="off"
          onKeyDown={handleKeyDown}>
          <TextField
            fullWidth
            label="Email Address"
            name="email"
            variant="outlined"
            color={theme.palette.mode === "light" ? "secondary" : "primary"}
            value={loginData.email}
            onChange={handleChange}
            onBlur={validateEmail}
            error={entryErrors.email}
            helperText={entryFeedback.email}
          />
          <TextField
            fullWidth
            label="Password"
            type="password"
            name="password"
            variant="outlined"
            color={theme.palette.mode === "light" ? "secondary" : "primary"}
            value={loginData.password}
            onChange={handleChange}
            onBlur={validatePassword}
            error={entryErrors.password}
            helperText={entryFeedback.password}
          />
        </Box>
        <Button
          disabled={invalid}
          variant="contained"
          onClick={submitSignIn}
          processing={processing}>
          Login
        </Button>
        <ErrorText>{errorFeedback}</ErrorText>
        {(errorFeedback === "Incorrect password." ||
          errorFeedback?.includes("Current passwords require") ||
          showForgotPassword) && (
          <TextLink theme={theme} onClick={handleForgotPassword}>
            Forgot your password?
          </TextLink>
        )}
        <TextLink theme={theme} onClick={() => navigate(ROUTES.SIGN_UP)}>
          Don't have an account?
        </TextLink>
      </FormCard>

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert
          onClose={() => setNotification((prev) => ({ ...prev, open: false }))}
          severity={notification.severity}
          variant="filled"
          sx={{ width: "100%" }}>
          {notification.message}
        </Alert>
      </Snackbar>
    </AuthLayout>
  );
};

const SignInPage = (props) => {
  if (props.stillLoading) return null;
  return <SignInComponent />;
};

export default SignInPage;
